import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UIMessage } from '../types/mastraTypes';
import { loadConversation, USE_HARDCODED_CONVERSATION } from '../api';
import { getHeadersWithUniqueVisitorId } from '../../Auth';
import { ConversationLoaderData } from '../types/conversation';

/** Sharing metadata returned when loading a shared or forked conversation */
export interface ConversationSharingInfo {
  isSharedView?: boolean;
  sharedConversationId?: string;
  sharedAtMessageIndex?: number;
  sharedByUserName?: string;
  sourceConversationId?: string;
  sourceCheckpointIndex?: number;
}

interface UseConversationLoaderProps {
  conversationId: string | null;
  sharedConversationId?: string | null;
  onConversationLoaded: (
    messages: UIMessage[],
    followUpQuestions: string[],
    sharingInfo?: ConversationSharingInfo,
  ) => void;
  onConversationNotFound: () => void;
  isActivelyStreaming?: boolean;
  conversationHistoryLength?: number;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
  /** Signed brand-view token from ?viewToken= URL param. Bypasses ownership check server-side. */
  viewToken?: string | null;
}

export const useConversationLoader = ({
  conversationId,
  sharedConversationId,
  onConversationLoaded,
  onConversationNotFound,
  isActivelyStreaming = false,
  conversationHistoryLength = 0,
  apiBaseUrl,
  viewToken,
}: UseConversationLoaderProps) => {
  const queryClient = useQueryClient();
  /** Tracks the last conversationId that was fully processed, to gate the enabled condition. */
  const loadedConversationIdRef = useRef<string | null>(null);
  /** Tracks the last sharedConversationId that was fully processed, to gate the enabled condition. */
  const loadedSharedConversationIdRef = useRef<string | null>(null);
  const loadingConversationIdRef = useRef<string | null>(null); // Track in-progress loads to prevent infinite loops
  const [forceReload, setForceReload] = useState(0);
  const previousConversationIdRef = useRef<string | null>(null);
  const wasStreamingRef = useRef(false);

  // Polling state for pending assistant responses (conversation loaded without assistant message)
  const [isPendingAssistantResponse, setIsPendingAssistantResponse] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const pollingStartTimeRef = useRef<number | null>(null);

  const POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds
  const POLLING_TIMEOUT_MS = 180000; // Stop after 3 minutes

  // Helper to set the correct ref based on which ID is active
  const markLoaded = (id: string | null) => {
    if (conversationId) {
      loadedConversationIdRef.current = id;
    } else if (sharedConversationId) {
      loadedSharedConversationIdRef.current = id;
    }
  };

  // Clear pending assistant response state when conversation changes
  useEffect(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, [conversationId, sharedConversationId]);

  // Allow loading when conversationId changes (e.g., from sidebar selection)
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    // Don't force reload if we're actively streaming
    if (isActivelyStreaming) {
      // Just mark as loaded to prevent future loads during this streaming session
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || 'hardcoded';
      }
      return;
    }

    // If we just finished streaming the SAME conversation, don't reload — we already have the data.
    // If conversationId changed (user switched conversations), skip this guard and load the new one.
    if (wasStreamingRef.current && !isActivelyStreaming && conversationId === prevId) {
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || 'hardcoded';
      }
      return;
    }

    // If hardcoded conversation is enabled and not loaded yet, trigger load
    if (USE_HARDCODED_CONVERSATION && conversationHistoryLength === 0 && !loadedConversationIdRef.current) {
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ['conversation', null, forceReload, USE_HARDCODED_CONVERSATION],
      });
      return;
    }

    const effectiveId = conversationId || sharedConversationId || null;
    const isConversationNew = !!conversationId && conversationId !== loadedConversationIdRef.current;
    const isSharedNew = !!sharedConversationId && sharedConversationId !== loadedSharedConversationIdRef.current;

    if (effectiveId && (isConversationNew || isSharedNew)) {
      // Check if the conversation is already loaded in the context.
      // This happens when navigating from widget to full page (prevId is null, first load).
      // Skip this guard when the user explicitly switched conversations (prevId !== null)
      // because chat.messages may not be fully cleared yet due to async state updates.
      if (conversationHistoryLength > 0 && prevId === null) {
        // Mark as loaded without triggering a query
        if (conversationId) loadedConversationIdRef.current = conversationId;
        else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
        return;
      }

      // Check if we're already loading this conversation (prevent infinite loop)
      if (loadingConversationIdRef.current === effectiveId) {
        return;
      }

      // Mark as loading to prevent re-triggers
      loadingConversationIdRef.current = effectiveId;

      // On the first open the query already runs for this key. A bump here would
      // change the key, drop that response and fetch the messages a second time.
      const currentKey = [
        'conversation',
        conversationId,
        sharedConversationId,
        forceReload,
        USE_HARDCODED_CONVERSATION,
      ];
      if (prevId === null && queryClient.getQueryData(currentKey) === undefined) {
        return;
      }

      // Force reload by incrementing state
      setForceReload((prev) => prev + 1);
      // Invalidate any cached conversation data to ensure fresh load
      queryClient.invalidateQueries({
        queryKey: ['conversation', conversationId, sharedConversationId],
      });
    } else if (
      effectiveId &&
      prevId === null &&
      ((!!conversationId && conversationId === loadedConversationIdRef.current) ||
        (!!sharedConversationId && sharedConversationId === loadedSharedConversationIdRef.current))
    ) {
      // Special case: conversationId was null and now matches the already-loaded conversation
      // This happens when clicking the same conversation - force a reload
      // Clear the ref so the `enabled` condition allows the query to run
      if (conversationId) loadedConversationIdRef.current = null;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = null;
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ['conversation', conversationId, sharedConversationId],
      });
    }
  }, [conversationId, sharedConversationId, queryClient, isActivelyStreaming, conversationHistoryLength, forceReload]);

  // Track previous streaming state for the next render cycle.
  useEffect(() => {
    wasStreamingRef.current = isActivelyStreaming;
  }, [isActivelyStreaming]);

  // Use React Query to load conversation
  const {
    data: loadedConversationData,
    isLoading: isLoadingConversationQuery,
    isSuccess: isConversationQuerySuccess,
    error: conversationLoadError,
  } = useQuery<ConversationLoaderData | null>({
    queryKey: ['conversation', conversationId, sharedConversationId, forceReload, USE_HARDCODED_CONVERSATION],
    queryFn: async () => {
      // If hardcoded conversation is enabled, load it even without conversationId
      if (USE_HARDCODED_CONVERSATION) {
        const headers = await getHeadersWithUniqueVisitorId();
        return loadConversation({
          conversationId: conversationId || 'hardcoded',
          headersToInject: headers,
          apiBaseUrl,
          viewToken,
        });
      }
      // Otherwise, require conversationId
      if (!conversationId) {
        if (!sharedConversationId) return null;
      }
      const headers = await getHeadersWithUniqueVisitorId();
      return loadConversation({
        conversationId,
        sharedConversationId,
        headersToInject: headers,
        apiBaseUrl,
        viewToken,
      });
    },
    enabled:
      ((USE_HARDCODED_CONVERSATION ||
        (!!conversationId && conversationId !== loadedConversationIdRef.current) ||
        (!!sharedConversationId && sharedConversationId !== loadedSharedConversationIdRef.current)) &&
        !isActivelyStreaming) ||
      (isPendingAssistantResponse && !isPollingTimedOut && !!conversationId && !isActivelyStreaming),
    retry: false,
    staleTime: Infinity,
    refetchInterval: isPendingAssistantResponse && !isPollingTimedOut ? POLLING_INTERVAL_MS : false,
  });

  // Process loaded conversation data
  useEffect(() => {
    // For hardcoded conversation, process even if conversationId is null
    const shouldProcess =
      loadedConversationData && (USE_HARDCODED_CONVERSATION || conversationId || sharedConversationId);
    if (shouldProcess) {
      const effectiveConversationId =
        conversationId || sharedConversationId || loadedConversationData?.conversationId || 'hardcoded';

      // Check if user is unauthorized to view this conversation
      if (loadedConversationData.error === 'unauthorized') {
        console.warn(`Unauthorized to access conversation ${effectiveConversationId}, redirecting`);
        onConversationNotFound();
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null; // Clear loading state
        // Optionally navigate to concierge home. Same-surface path: honor the
        // App Proxy sub-path injected by the back-room skeleton (e.g. `/a/sos`)
        // so the rewritten URL stays refreshable on-store; '' in front-room.
        if (typeof window !== 'undefined') {
          const routePath = (window as unknown as { __BC_ROUTE_PATH__?: string }).__BC_ROUTE_PATH__ ?? '';
          window.history.pushState({}, '', `${routePath.replace(/\/+$/, '')}/concierge`);
        }
        return;
      }

      const processedMessages: UIMessage[] = (loadedConversationData.messages || [])
        .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
        .filter((msg) => {
          if (msg.role === 'user') return true;
          return Array.isArray(msg.parts) && msg.parts.length > 0;
        });
      const followUpQuestions = loadedConversationData.followUpQuestions || [];
      const conversationStatus = loadedConversationData.status || 'completed';

      if (processedMessages.length > 0) {
        // Extract sharing metadata from the loaded conversation
        const sharingInfo: ConversationSharingInfo = {
          isSharedView: loadedConversationData.isSharedView,
          sharedConversationId: loadedConversationData.sharedConversationId,
          sharedAtMessageIndex: loadedConversationData.sharedAtMessageIndex,
          sharedByUserName: loadedConversationData.sharedByUserName,
          sourceConversationId: loadedConversationData.sourceConversationId,
          sourceCheckpointIndex: loadedConversationData.sourceCheckpointIndex,
        };

        onConversationLoaded(processedMessages, followUpQuestions, sharingInfo);
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null; // Clear loading state

        // Detect if assistant response is still pending (last message is from user).
        // Voice-shape user messages (id starts with 'voice-user-') are excluded —
        // voice replies arrive via the next refetch, not via text streaming, so the
        // loading indicator would mislead after a voice call ends (Plan 8 / D-71).
        const lastProcessedMessage = processedMessages[processedMessages.length - 1];
        const lastIsVoiceUserMessage = (lastProcessedMessage as any)?.id?.startsWith?.('voice-user-') === true;
        if (lastProcessedMessage?.role === 'user' && !lastIsVoiceUserMessage) {
          // Backend hasn't generated assistant response yet — start polling
          if (!pollingStartTimeRef.current) {
            pollingStartTimeRef.current = Date.now();
          }
          setIsPendingAssistantResponse(true);
        } else {
          // Assistant response present (or last message is a voice turn) — stop polling
          setIsPendingAssistantResponse(false);
          setIsPollingTimedOut(false);
          pollingStartTimeRef.current = null;
        }
      } else if (conversationStatus === 'in_progress') {
        // Thread exists but assistant response is still being generated.
        onConversationLoaded([], followUpQuestions, {
          isSharedView: loadedConversationData.isSharedView,
          sharedConversationId: loadedConversationData.sharedConversationId,
          sharedAtMessageIndex: loadedConversationData.sharedAtMessageIndex,
          sharedByUserName: loadedConversationData.sharedByUserName,
          sourceConversationId: loadedConversationData.sourceConversationId,
          sourceCheckpointIndex: loadedConversationData.sourceCheckpointIndex,
        });
        if (!pollingStartTimeRef.current) {
          pollingStartTimeRef.current = Date.now();
        }
        setIsPendingAssistantResponse(true);
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null;
      } else {
        // Conversation not found - notify parent
        console.warn(`Conversation ${effectiveConversationId} not found, clearing URL`);
        onConversationNotFound();
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null; // Clear loading state
      }
    } else if (
      isConversationQuerySuccess &&
      loadedConversationData === null &&
      (conversationId || sharedConversationId) &&
      (conversationId
        ? loadedConversationIdRef.current !== conversationId
        : loadedSharedConversationIdRef.current !== sharedConversationId)
    ) {
      // Query resolved with null — conversation does not exist
      const effectiveId = conversationId || sharedConversationId;
      console.warn(`Conversation ${effectiveId} not found (null response), clearing URL`);
      onConversationNotFound();
      if (conversationId) loadedConversationIdRef.current = conversationId;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
      loadingConversationIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedConversationData, conversationId, sharedConversationId, isConversationQuerySuccess]);

  // Handle conversation load errors
  useEffect(() => {
    if (conversationLoadError && (conversationId || sharedConversationId)) {
      console.error('Failed to load conversation:', conversationLoadError);
      onConversationNotFound();
      if (conversationId) loadedConversationIdRef.current = conversationId;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
      loadingConversationIdRef.current = null; // Clear loading state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationLoadError, conversationId, sharedConversationId]);

  // Polling timeout: stop after POLLING_TIMEOUT_MS
  useEffect(() => {
    if (!isPendingAssistantResponse || isPollingTimedOut) return;

    const timer = setTimeout(() => {
      setIsPollingTimedOut(true);
      setIsPendingAssistantResponse(false);
    }, POLLING_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isPendingAssistantResponse, isPollingTimedOut]);

  /** Reset pending/timeout state (used by retry handler) */
  const resetPendingState = useCallback(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, []);

  /**
   * Force the next render to refetch the conversation from the server.
   * Used when we detect that we may have missed events delivered while the
   * tab was disconnected (e.g. the support events SSE reconnected after a
   * transient network drop). The existing `enabled` gate already includes
   * `!isActivelyStreaming`, so a refetch triggered during an active LLM
   * stream defers until streaming finishes — no new race window here.
   */
  const markConversationStale = useCallback(() => {
    loadedConversationIdRef.current = null;
    loadedSharedConversationIdRef.current = null;
    setForceReload((prev) => prev + 1);
  }, []);

  return {
    isLoadingConversationQuery,
    conversationLoadError,
    isPendingAssistantResponse,
    isPollingTimedOut,
    resetPendingState,
    markConversationStale,
  };
};
